import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { orderFormsAPI, ordersAPI } from '@/lib/api';
import { PhoneCaptureButton } from '@/components/PhoneCapture';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Camera, Check } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

export default function OrderFormPhotoImportDialog({ open, onOpenChange, onCreated, sites = [] }) {
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState('');
    const [siteId, setSiteId] = useState('');
    const [file, setFile] = useState([]);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [creating, setCreating] = useState(false);

    const FUJI_ID = 'fuji-express';
    const selectedTpl = templates.find(t => t.id === templateId);
    const needsSite = selectedTpl?.site_id === FUJI_ID;
    const feSites = sites.filter(s => s.name.startsWith('FE '));

    useEffect(() => {
        if (open) {
            orderFormsAPI.templates().then(r => setTemplates(r.data))
                .catch(() => toast.error('Impossible de charger les formulaires — vérifiez la connexion et réessayez'));
            setResult(null); setFile([]); setSiteId('');
        }
    }, [open]);

    const analyze = async () => {
        if (!templateId || !file.length) { toast.error('Choisissez le formulaire et la ou les photos'); return; }
        if (needsSite && !siteId) { toast.error('Choisissez le site Fuji Express'); return; }
        setLoading(true);
        try {
            const res = await orderFormsAPI.importPhoto(templateId, file);
            setResult({ ...res.data, lines: res.data.lines.map(l => ({ ...l, checked: true })) });
            if (res.data.lines.length === 0) toast.warning('Aucune quantité manuscrite détectée');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur d\'analyse');
        } finally { setLoading(false); }
    };

    const createOrders = async () => {
        setCreating(true);
        try {
            const bySupplier = {};
            result.lines.filter(l => l.checked && l.quantity > 0).forEach(l => {
                (bySupplier[l.supplier_id] = bySupplier[l.supplier_id] || []).push(l);
            });
            let created = 0;
            for (const [supplierId, lines] of Object.entries(bySupplier)) {
                await ordersAPI.create({
                    supplier_id: supplierId,
                    site_id: result.site_id === FUJI_ID ? siteId
                        : result.site_id.startsWith('myfood') ? (sites.find(s => s.name === 'My Food')?.id || result.site_id)
                            : result.site_id,
                    notes: 'Import formulaire photo',
                    items: lines.map(l => ({
                        supplier_product_id: l.supplier_product_id,
                        industrial_product_id: l.industrial_product_id,
                        quantity: l.quantity,
                        unit_price: l.unit_price || 0,
                        supplier_product_name: l.label,
                    })),
                });
                created++;
            }
            toast.success(`${created} commande(s) créée(s) en brouillon`);
            onCreated && onCreated();
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de création');
        } finally { setCreating(false); }
    };

    const grouped = result ? result.lines.reduce((acc, l, i) => {
        (acc[l.supplier_name] = acc[l.supplier_name] || []).push({ ...l, _i: i });
        return acc;
    }, {}) : {};

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="order-form-photo-dialog">
                <DialogHeader><DialogTitle><Camera className="w-5 h-5 inline mr-2" />Import formulaire photo</DialogTitle></DialogHeader>

                {!result ? (
                    <div className="space-y-3">
                        <Select value={templateId} onValueChange={setTemplateId}>
                            <SelectTrigger data-testid="photo-template-select"><SelectValue placeholder="Choisir le formulaire…" /></SelectTrigger>
                            <SelectContent>
                                {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        {needsSite && (
                            <Select value={siteId} onValueChange={setSiteId}>
                                <SelectTrigger data-testid="photo-site-select"><SelectValue placeholder="Site Fuji Express…" /></SelectTrigger>
                                <SelectContent>
                                    {feSites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        )}
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Input type="file" accept="image/*" multiple className="flex-1" onChange={e => setFile(Array.from(e.target.files || []))} data-testid="photo-file-input" />
                            <PhoneCaptureButton
                                className="shrink-0"
                                testId="order-form-phone-btn"
                                onPhotos={(fs) => setFile(prev => [...prev, ...fs])}
                            />
                        </div>
                        {file.length > 1 && <p className="text-xs text-slate-500">{file.length} pages sélectionnées</p>}
                        <Button className="btn-primary w-full" onClick={analyze} disabled={loading} data-testid="photo-analyze-btn">
                            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Lecture IA en cours…</> : `Analyser ${file.length > 1 ? `les ${file.length} photos` : 'la photo'}`}
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-3 min-w-0">
                        {Object.entries(grouped).map(([sup, lines]) => (
                            <div key={sup} className="border rounded-lg overflow-x-auto">
                                <p className="px-3 py-1.5 bg-slate-100 font-bold text-sm rounded-t-lg">{sup} — {lines.filter(l => l.checked).length} ligne(s)</p>
                                {lines.map(l => (
                                    <div key={l._i} className="flex items-center gap-2 px-3 py-1 text-sm border-t" data-testid={`photo-line-${l._i}`}>
                                        <Checkbox checked={l.checked} onCheckedChange={v => setResult(p => ({ ...p, lines: p.lines.map((x, i) => i === l._i ? { ...x, checked: !!v } : x) }))} />
                                        <span className="flex-1 truncate min-w-0">{l.label}</span>
                                        <Input type="number" value={l.quantity}
                                            onChange={e => setResult(p => ({ ...p, lines: p.lines.map((x, i) => i === l._i ? { ...x, quantity: parseFloat(e.target.value) || 0 } : x) }))}
                                            className="w-16 h-7 text-right shrink-0 font-semibold border-slate-300 bg-amber-50" data-testid={`photo-qty-${l._i}`} />
                                        <span className="text-xs text-slate-400 w-16 text-right shrink-0 hidden sm:block">{l.unit_price ? formatCurrency(l.unit_price) : ''}</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                        {result.unmatched?.length > 0 && (
                            <p className="text-xs text-amber-600">⚠️ Non reconnus : {result.unmatched.map(u => `${u.produit} (${u.quantity})`).join(', ')}</p>
                        )}
                        <div className="flex flex-wrap gap-2 justify-end">
                            <Button variant="outline" onClick={() => setResult(null)}>Reprendre</Button>
                            <Button className="btn-primary" onClick={createOrders} disabled={creating || result.lines.filter(l => l.checked).length === 0} data-testid="photo-create-orders-btn">
                                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                                Créer les commandes ({new Set(result.lines.filter(l => l.checked).map(l => l.supplier_id)).size} fournisseur(s))
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
